import { useState } from 'react'
import { Check, ChevronDown, Copy, Loader2, RefreshCw, Sparkles, X } from 'lucide-react'

import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'
import { draftAreaNarrative, type AreaAnchorType, type AreaNarrative, type ScoreEvidence } from 'services/aiService'

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

/** "2 demographic areas · 3 saved retailers (1 co-tenant, 1 competitor)" */
export const basedOn = ({ demographics, retailers, cotenants, competitors }: AreaNarrative['basedOn']): string => {
	const roles = [cotenants && plural(cotenants, 'co-tenant'), competitors && plural(competitors, 'competitor')].filter(Boolean).join(', ')
	return [demographics ? plural(demographics, 'demographic area') : 'no demographics', retailers ? `${plural(retailers, 'saved retailer')}${roles ? ` (${roles})` : ''}` : 'no saved retailers'].join(' · ')
}

/** Every fact the summary cites, once each, in the order first cited. */
export const citedFacts = (narrative: AreaNarrative): ScoreEvidence[] => {
	const seen = new Map<string, ScoreEvidence>()
	for (const paragraph of narrative.paragraphs) for (const sentence of paragraph.sentences) for (const fact of sentence.evidence) if (!seen.has(fact.id)) seen.set(fact.id, fact)
	return [...seen.values()]
}

const messageOf = (error: any): string => error?.response?.data?.message || "Ask MDT couldn't write an area summary just now. Try again in a moment."

/**
 * "Draft area summary" (MYD-646): a few paragraphs on who lives around a
 * location or an evaluation site and what retail is near it, from its
 * demographics and saved Other Retailers. Read, copy, regenerate; saved
 * nowhere, like the comparison draft (MYD-639).
 *
 * Every sentence the server returns cites facts, so the sources list below the
 * text is what the summary rests on, nothing more.
 */
export function AreaNarrativePanel({
	anchorType,
	anchorId,
	className,
	draft = draftAreaNarrative,
}: {
	anchorType: AreaAnchorType
	anchorId: string
	className?: string
	draft?: typeof draftAreaNarrative
}) {
	const [result, setResult] = useState<AreaNarrative | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [copied, setCopied] = useState(false)
	const [showSources, setShowSources] = useState(false)

	const write = async () => {
		setBusy(true)
		setError(null)
		setCopied(false)
		try {
			setResult(await draft(anchorType, anchorId))
		} catch (failure) {
			setError(messageOf(failure))
		} finally {
			setBusy(false)
		}
	}

	const copy = async () => {
		if (!result) return
		try {
			await navigator.clipboard.writeText(result.text)
			setCopied(true)
		} catch {
			setError('Copy did not work here: select the text and copy it instead.')
		}
	}

	if (!result) {
		return (
			<div className={cn('flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3', className)}>
				<Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
				<p className="min-w-0 flex-1 text-sm text-gray-600">A short summary of the people and the retail around this site, from its demographics and saved Other Retailers.</p>
				<Button type="button" variant="ghost" onClick={write} disabled={busy}>
					{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />}
					{busy ? 'Drafting…' : 'Draft area summary'}
				</Button>
				{error && (
					<p role="alert" className="w-full text-xs text-destructive">
						{error}
					</p>
				)}
			</div>
		)
	}

	const sources = citedFacts(result)

	return (
		<section aria-label="Area summary" className={cn('rounded-lg border border-gray-200 bg-white p-4', className)}>
			<div className="flex flex-wrap items-start justify-between gap-2">
				<div>
					<h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
						<Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
						Area summary
					</h4>
					<p className="mt-0.5 text-xs text-gray-500">AI draft: check the figures before you use it. Based on {basedOn(result.basedOn)}.</p>
				</div>
				<div className="flex items-center gap-1">
					<Button type="button" variant="ghost" size="input" onClick={copy}>
						{copied ? <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="mr-1 h-3.5 w-3.5" aria-hidden="true" />}
						{copied ? 'Copied' : 'Copy'}
					</Button>
					<Button type="button" variant="ghost" size="input" onClick={write} disabled={busy}>
						{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />}
						Regenerate
					</Button>
					<Button type="button" variant="ghost" size="input" onClick={() => setResult(null)} aria-label="Close the area summary">
						<X className="h-3.5 w-3.5" aria-hidden="true" />
					</Button>
				</div>
			</div>
			{error && (
				<p role="alert" className="mt-2 text-xs text-destructive">
					{error}
				</p>
			)}
			<div className="mt-3 space-y-3 text-sm text-gray-800">
				{result.paragraphs.map((paragraph, index) => (
					<p key={index}>{paragraph.sentences.map(sentence => sentence.text).join(' ')}</p>
				))}
			</div>
			{sources.length > 0 && (
				<div className="mt-3 border-t border-gray-100 pt-2">
					<button type="button" onClick={() => setShowSources(open => !open)} aria-expanded={showSources} className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-primary">
						<ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showSources && 'rotate-180')} aria-hidden="true" />
						{showSources ? 'Hide sources' : `Show ${plural(sources.length, 'source')}`}
					</button>
					{showSources && (
						<ul className="mt-2 space-y-1">
							{sources.map(fact => (
								<li key={fact.id} className="text-xs text-gray-600">
									{fact.text}
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</section>
	)
}

export default AreaNarrativePanel
